import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { analysisHistoryLabel, apiService, AnalysisHistoryItem } from '../services/apiService';
import './Reports.css';

/** Shown while the server builds the PDF (one request, ~20–60 s): no real percentage exists, so the bar is indeterminate. */
const ReportProgress: React.FC<{ action: 'download' | 'email'; withPdf: boolean; elapsed: number }> = ({ action, withPdf, elapsed }) => {
  const title = action === 'download'
    ? 'Generating your PDF report…'
    : withPdf ? 'Generating the PDF and sending the email…' : 'Sending the email…';
  const detail = !withPdf
    ? 'This only takes a few seconds.'
    : elapsed < 60
      ? 'Drawing satellite maps and laying out the report. This usually takes 20–60 seconds — please keep this page open.'
      : 'Taking longer than usual. Large uploads can need up to 3 minutes — please keep this page open.';
  return (
    <div className="report-progress" role="status" aria-live="polite">
      <div className="report-progress-head">
        <span className="report-spinner" aria-hidden="true"></span>
        <strong>{title}</strong>
        <span className="report-progress-time">{elapsed}s</span>
      </div>
      <div className="report-progress-track" aria-hidden="true">
        <div className="report-progress-bar"></div>
      </div>
      <p className="report-progress-detail">{detail}</p>
    </div>
  );
};

const Reports: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [subject, setSubject] = useState('EUDR Compliance Analysis Results');
  const [includeReport, setIncludeReport] = useState(true);
  const [includeGeoJSON, setIncludeGeoJSON] = useState(false);
  const [sending, setSending] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userEmail, setUserEmail] = useState<string>('');
  const [customerId, setCustomerId] = useState('');
  const [elapsed, setElapsed] = useState(0);
  const busy = sending || downloading;

  // Elapsed-time counter for the progress panel
  useEffect(() => {
    if (!busy) return;
    setElapsed(0);
    const started = Date.now();
    const timer = setInterval(() => setElapsed(Math.floor((Date.now() - started) / 1000)), 1000);
    return () => clearInterval(timer);
  }, [busy]);
  const [history, setHistory] = useState<AnalysisHistoryItem[]>([]);
  // '' = latest analysis
  const [analysisId, setAnalysisId] = useState<string>(searchParams.get('analysis_id') || '');

  useEffect(() => {
    // Check authentication status
    const checkAuth = async () => {
      const authenticated = apiService.isAuthenticated();
      setIsAuthenticated(authenticated);
      
      if (authenticated) {
        try {
          const userData = apiService.getStoredUser();
          if (userData) {
            setUserEmail(userData.email);
            setCustomerId(userData.customer_id);
          }
          apiService.getAnalysisHistory().then(setHistory).catch((e) => console.error(e));
        } catch (error) {
          console.error('Error getting user data:', error);
        }
      } else {
        // For non-authenticated users, get customer ID from URL
        const urlCustomerId = searchParams.get('customer_id');
        if (urlCustomerId) {
          setCustomerId(urlCustomerId);
        }
      }
    };
    
    checkAuth();
  }, [searchParams]);

  const handleSendEmail = async () => {
    if (isAuthenticated) {
      // For authenticated users, send to their registered email
      if (!userEmail) {
        setMessage({ type: 'error', text: 'User email not found' });
        return;
      }
    } else {
      setMessage({ type: 'error', text: 'Email reports are only available for authenticated users' });
      return;
    }

    setSending(true);
    setMessage(null);

    try {
      const result = await apiService.sendEmailAuthenticated({
        subject,
        include_report: includeReport,
        include_geojson: includeGeoJSON,
        analysis_id: analysisId || undefined,
      });
      
      const message = result?.message || `Email sent successfully to ${userEmail}!`;
      setMessage({ type: 'success', text: message });
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error instanceof Error ? error.message : 'Failed to send email'
      });
    } finally {
      setSending(false);
    }
  };

  const handleDownloadReport = async () => {
    setDownloading(true);
    setMessage(null);

    try {
      if (isAuthenticated) {
        const blob = await apiService.downloadReportAuthenticated(analysisId || undefined);
        
        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `EUDR_Report_${customerId}_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(link);
        link.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
        
        setMessage({ type: 'success', text: 'Report downloaded successfully!' });
      } else {
        setMessage({ type: 'error', text: 'Download feature is only available for authenticated users' });
      }
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error instanceof Error ? error.message : 'Failed to download report'
      });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="reports-page">
      <h1>Generate Reports</h1>
      <p className="page-description">
        {isAuthenticated 
          ? `Send EUDR compliance analysis reports to your registered email (${userEmail})`
          : 'Please log in to generate and send reports'
        }
      </p>

      <div className="reports-container">
        {/* Authenticated User Info */}
        {isAuthenticated ? (
          <div className="user-info-section">
            <h3>User Information</h3>
            <div className="user-info">
              <div className="info-item">
                <span className="label">Email:</span>
                <span className="value">{userEmail}</span>
              </div>
              <div className="info-item">
                <span className="label">Customer ID:</span>
                <span className="value">{customerId}</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="auth-required">
            <h3>Authentication Required</h3>
            <p>Please log in to access report generation features.</p>
            <button 
              onClick={() => window.location.href = '/login'}
              className="btn primary"
            >
              Go to Login
            </button>
          </div>
        )}

        {/* Report Options - Only show for authenticated users */}
        {isAuthenticated && (
          <div className="report-actions-section">
            <h3>Report Options</h3>

            {history.length > 0 && (
              <div className="form-group">
                <label htmlFor="report-analysis">Report for</label>
                <select id="report-analysis" value={analysisId} onChange={(e) => setAnalysisId(e.target.value)}>
                  {history.map((a) => (
                    <option key={a.analysis_id} value={a.is_latest ? '' : a.analysis_id}>{analysisHistoryLabel(a)}</option>
                  ))}
                </select>
              </div>
            )}
            
            <div className="form-group">
              <label htmlFor="subject">Email Subject:</label>
              <input
                type="text"
                id="subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Email subject"
              />
            </div>

            <div className="form-group">
              <div className="checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={includeReport}
                    onChange={(e) => setIncludeReport(e.target.checked)}
                  />
                  Include PDF Report
                </label>
                
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={includeGeoJSON}
                    onChange={(e) => setIncludeGeoJSON(e.target.checked)}
                  />
                  Include GeoJSON Files
                </label>
              </div>
            </div>

            <div className="action-buttons">
              <button 
                onClick={handleSendEmail} 
                disabled={busy || !userEmail}
                className="btn primary"
              >
                {sending ? 'Sending...' : `Send Email Report to ${userEmail}`}
              </button>
              
              <button 
                onClick={handleDownloadReport}
                disabled={busy}
                className="btn secondary"
              >
                {downloading ? 'Preparing...' : 'Download Report'}
              </button>
            </div>

            {busy && (
              <ReportProgress action={downloading ? 'download' : 'email'} withPdf={downloading || includeReport} elapsed={elapsed} />
            )}

            {message && (
              <div className={`message ${message.type}`}>
                {message.type === 'success' ? '✓' : '✗'} {message.text}
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default Reports;